/**
 * Q5 camera shake — pure math for a one-shot kill-impact shake.
 *
 * On a kill rising edge, the camera offsets by a small seeded-random
 * vector that decays linearly back to neutral over `SHAKE_DURATION_MS`.
 *
 * Determinism: the shake's *direction* is seeded by `(matchSeed,
 * victim_tick)` so two viewings of the same replay produce the same
 * direction. The wall-clock `startedAt` controls only timing, not
 * shape.
 */

import { SeededRng } from "../determinism/cosmeticSeed";

export const SHAKE_DURATION_MS = 250;
export const SHAKE_AMPLITUDE = 0.05;

export interface ShakeOffset {
  dx: number;
  dy: number;
  dz: number;
}

const REST: ShakeOffset = { dx: 0, dy: 0, dz: 0 };

/**
 * Camera-position offset at `nowMs` given a shake that started at
 * `startedAt` with deterministic `seed`. Returns rest when no shake is
 * active. Magnitude decays linearly over the shake window. The seeded
 * direction is a unit vector on the unit sphere (with Y-bias toward
 * 0 so the shake reads sideways rather than up-and-down).
 */
export function shakeOffset(
  startedAt: number | null,
  seed: number,
  nowMs: number,
): ShakeOffset {
  if (startedAt === null) return REST;
  const elapsed = nowMs - startedAt;
  if (elapsed < 0 || elapsed >= SHAKE_DURATION_MS) return REST;
  const decay = 1 - elapsed / SHAKE_DURATION_MS;
  // Seeded random unit vector — sideways-biased.
  const rng = new SeededRng(seed >>> 0);
  // Use two random values for X/Z direction; Y stays small to avoid
  // an up-down jolt that hurts readability.
  const theta = rng.next() * Math.PI * 2;
  const yJitter = (rng.next() - 0.5) * 0.4; // ±20% of unit
  const amp = SHAKE_AMPLITUDE * decay;
  return {
    dx: Math.cos(theta) * amp,
    dy: yJitter * amp,
    dz: Math.sin(theta) * amp,
  };
}
